import classNames from "classnames";
import { Utils } from "common/utils";
import { ButtonSecondary } from "components/Atoms/Button";
import { FilterContext, FilterProvider, FilterProviderRef, FilterValues } from "components/filters/FilterContext";
import FilterHeader from "components/filters/FilterHeader";
import ArtistFilter from "components/filters/Filters/ArtistFilter";
import GroupByPeriodFilter from "components/filters/Filters/GroupByPeriodFilter";
import { getCacheFriendlyInstruction } from "components/filters/Filters/InstructionHelpers";
import SingleBookkeepingFilter from "components/filters/Filters/SingleBookkeepingFilter";
import SingleOwnerFilter from "components/filters/Filters/SingleOwnerFilter";
import StatusFilter from "components/filters/Filters/StatusFilter";
import TagFilter from "components/filters/Filters/TagFilter";
import { mapArtistFilterToInstruction, mapGroupByPeriodFilterToInstruction, mapResponseToArtistFilter, mapResponseToGroupByPeriodFilter, mapResponseToOwnerFilter, mapResponseToSingleBookkeepingFilter, mapResponseToStatusFilter, mapResponseToTagFilter, mapSingleBookkeepingFilterToInstruction, mapSingleOwnerFilterToInstruction, mapStatusFilterToInstruction, mapTagFilterToInstruction } from "components/filters/mappingUtils";
import { NoDataAvailableWarning } from "components/NoDataAvailableDiv";
import { Loading, Panel } from "components/panels";
import Table from "components/Tables/Table";
import React, { useContext, useRef, useState } from "react";
import { useLoaderData } from "react-router";
import { getMoneyFormatter } from "utilities/formatter";
import { useCustomQuery } from "utilities/hookutils";
import { SalesChart } from "./SalesChart";

const mapFilterValuesToInstruction = (values: FilterValues): server.dto.ListShowTotalInstruction => ({
  bookkeepingId: mapSingleBookkeepingFilterToInstruction(values.bookkeepings),
  artistIds: mapArtistFilterToInstruction(values.artist),
  tags: mapTagFilterToInstruction(values.tags),
  statuses: mapStatusFilterToInstruction(values.statuses),
  ownerUserId: mapSingleOwnerFilterToInstruction(values.owners),
  groupByPeriod: mapGroupByPeriodFilterToInstruction(values.groupByPeriod),
});

const mapResponseToFilterValues = (response: server.dto.ListShowTotalFilters, values: FilterValues): Partial<FilterValues> => ({
  bookkeepings: mapResponseToSingleBookkeepingFilter(response.bookkeeping),
  artist: mapResponseToArtistFilter(response.artists),
  tags: mapResponseToTagFilter(response.tags, values.tags),
  statuses: mapResponseToStatusFilter(response.statuses, values.statuses),
  owners: mapResponseToOwnerFilter(response.ownerUsers),
  groupByPeriod: mapResponseToGroupByPeriodFilter(response.groupByPeriod)
});

const getDefaultVisibleProducts = (data: server.dto.GroupedShowTotals[]): string[] => {
  const allSalesProductTypes = data
    .map(d => d.salesSum)
    .reduce((acc, curr) => acc.concat(curr), []); // map+reduce is like doing SelectMany in C#
  const uniqueProductTypes = Utils.uniqBy(allSalesProductTypes, p => p.productType.value);

  return uniqueProductTypes
    .filter(prod => ["artistFee", "bookingFee"].includes(prod.productType.value))
    .map(prod => prod.productType.text);
}

export const ShowTotalsApp = () => {
  const viewModel = useLoaderData<server.viewmodel.ShowTotalFrontModel>();
  const filterRef = React.useRef<FilterProviderRef>(null);

  const [instruction, onInstructionChange] = useState<server.dto.ListShowTotalInstruction>({
    ...viewModel.initialInstruction,
    cache: "show_Total",
  });
  const [initialLoading, setInitialLoading] = useState(true);

  const api = useCustomQuery({
    queryFn: async () => {
      return window.CoreApi.Show.GetTotals(getCacheFriendlyInstruction(instruction));
    },
    enabled: !!instruction.bookkeepingId,
    queryKey: ["showTotals", instruction],
    onSuccess: (response) => {
      if (initialLoading) {
        filterRef.current.setDefaultValues((prev: FilterValues) => {
          return mapResponseToFilterValues(viewModel.defaultFilters, prev);
        });
      }
      filterRef.current.updateValues((prev: FilterValues) => {
        return mapResponseToFilterValues(response.data.request, prev);
      });
      setInitialLoading(false);
    },
    cacheTime: 0
  });

  const onFilterChange = (filter: FilterValues) => {
    const newInstruction = mapFilterValuesToInstruction(filter);
    onInstructionChange({
      ...newInstruction,
      cache: "show_Total"
    });
  };

  return <FilterProvider
    ref={filterRef}
    onFilterChange={onFilterChange}
  >
    <div className="flex-column panel-gap">
      <FilterHeader.Filters>
        <div className="selectiontool-header-filters">
          <SingleBookkeepingFilter initialBookkeeping={viewModel.defaultFilters.bookkeeping} />
          <ArtistFilter />
          <TagFilter mainArea="show" initialTags={viewModel.tags} />
          {viewModel.statuses.length > 1 && (
            <StatusFilter initialStatuses={viewModel.statuses} />
          )}
          <SingleOwnerFilter />
          <GroupByPeriodFilter />
        </div>
      </FilterHeader.Filters>

      <TableAndChart
        items={api.data?.data.data ?? []}
        isLoading={api.isLoading}
      />
    </div>
  </FilterProvider>
}

interface TableAndChartProps {
  items: server.dto.GroupedShowTotals[]
  isLoading: boolean
}

const TableAndChart = (props: TableAndChartProps) => {
  const filterContext = useContext(FilterContext);
  const seriesVisible = useRef<string[]>([]);
  const [initialSetVisibleSeries, setInitialSetVisibleSeries] = React.useState(false);
  const moneyFormatter = getMoneyFormatter({
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  React.useEffect(() => {
    // We want to set the default visible products as soon as we have data, but we don't want to override the user's selection of visible products after that
    if (props.items.length > 0 && !initialSetVisibleSeries) {
      seriesVisible.current = getDefaultVisibleProducts(props.items);
      setInitialSetVisibleSeries(true);
    }
  }, [props.items]);

  const selectedBookkeepings = filterContext.getValue("bookkeepings").selectedBookkeepings;
  const selectedBookkeeping = selectedBookkeepings.length > 0 ? selectedBookkeepings[0] : null;
  const currency = selectedBookkeeping ? selectedBookkeeping.baseCurrency : "";

  if (!selectedBookkeeping) {
    return <NoDataAvailableWarning>
      {RESX.Bookkeeping.SelectABookkeeping}
    </NoDataAvailableWarning>;
  }

  if (props.isLoading) {
    return <Loading />;
  }

  const allProducts = props.items.map(d => d.salesSum).reduce((acc, curr) => acc.concat(curr), []);
  const uniqueProducts = Utils.uniqBy(allProducts, p => p.productType.text);
  const uniqueProductValues = uniqueProducts.map(p => p.productType.text).sort();

  const productValueMap: Record<string, { data: number[] }> = {};
  for (const prod of uniqueProductValues) {
    productValueMap[prod] = { data: [] };
  }

  const totalPerTimeframe = props.items.map(date => {
    let totalForDate = 0;

    for (const prod of date.salesSum) {
      const data = productValueMap[prod.productType.text];
      data.data.push(prod.amount);

      totalForDate = totalForDate + prod.amount;
    }

    return totalForDate;
  });

  const products = Object.keys(productValueMap).map(key => ({
    name: key,
    data: productValueMap[key].data,
    percentageChanges: [],
    total: 0,
  }));

  let totalOfTotals = 0;
  for (const product of products) {
    for (let i = 0; i < product.data.length; i++) {
      const currData = product.data[i];
      product.total += currData;

      if (i > 0) {
        const prevData = product.data[i - 1];
        const diffPercentage = getChangeInPercent(prevData, currData);
        product.percentageChanges.push(diffPercentage);
      }
    }

    totalOfTotals += product.total;
  }

  const toCsv = () => {
    const result: Array<Array<string | number>> = [];

    result.push([RESX.Product.Products, ...props.items.map(d => d.label), RESX.Financial.Total]);
    for (const product of products) {
      result.push([product.name, ...product.data, product.total]);
    }

    result.push([RESX.Financial.Total, ...totalPerTimeframe.map(t => t), totalOfTotals]);
    const csvContent = result.map(e => e.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sales.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return <>
    <Panel
      title={RESX.Financial.SalesTotal}
      panelClassName="s1_panel"
      button={<ButtonSecondary label={RESX.Show.ExportToCSV} onClick={toCsv} />}
    >
      <div style={{
        overflowX: "auto",
      }}>
        <Table
          items={products}
          isLoading={props.isLoading}
          columns={[{
            key: "product",
            header: RESX.Product.Products,
            render: item => item.name,
            size: "minmax(auto, 1fr)",
          },
          ...props.items.map((item, index) => ({
            key: `data-${item.label}`,
            header: item.label,
            className: "s1_cell_financialamount",
            render: x => <div>
              <div>{moneyFormatter.format(x.data[index])} {currency.toUpperCase()}</div>
              {index > 0 && <div>{getDiffDiv(x.percentageChanges[index - 1])}</div>}
            </div>,
            size: "minmax(auto, 1fr)" as any
          })),
          {
            key: "total",
            header: RESX.Financial.Total,
            className: "s1_cell_financialamount",
            render: item => <>{moneyFormatter.format(item.total)} {currency.toUpperCase()}</>,
            size: "minmax(auto, 1fr)"
          }]}
          footers={[{
            key: "total",
            className: "s1_cell_financialamount panel-header-bg",
            entries: [
              null,
              ...totalPerTimeframe.map(t => `${moneyFormatter.format(t)} ${currency.toUpperCase()}`),
              `${moneyFormatter.format(totalOfTotals)} ${currency.toUpperCase()}`
            ],
          }]}
          getKey={item => item.name}
        />
      </div>
    </Panel>

    {initialSetVisibleSeries && <Panel
      panelClassName="s1_panel"
      title={RESX.Financial.SalesTotal}
    >
      <SalesChart
        seriesVisible={seriesVisible.current}
        setSeriesVisible={(setFunc) => {
          seriesVisible.current = setFunc(seriesVisible.current);
        }}
        data={props.items}
        currency={currency}
      />
    </Panel>}
  </>
}

const getChangeInPercent = (oldValue: number, newValue: number) => ((newValue - oldValue) / oldValue) * 100;

const getDiffDiv = (percentChange: number) => {
  // is the old value is zero, there will be a division by 0
  if (!isFinite(percentChange)) {
    return <></>;
  }

  const classes = classNames({
    s1_green: percentChange > 0,
    s1_red: percentChange < 0
  });

  return <div className={classes}>
    {percentChange.toFixed(0)}%
  </div>;
};